import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import {
  Button,
  Col,
  Pagination,
  Row,
  Table,
  Tag,
  Modal,
  Input,
  message,
  Space,
  Tooltip,
  Select,
} from 'antd'
import {
  ExclamationCircleOutlined,
  FormOutlined,
  DeleteOutlined,
  MailOutlined,
} from '@ant-design/icons'
import { useGlobalStore } from 'stores/global'
import { Contact } from 'stores/types/contact.types'
import { findContacts, deleteContact } from 'services/contact.service'
import { DatePicker } from 'components/features/picker'
import { useAuth } from 'stores/authorized'
import PermissionAccess from 'components/PermissionAccess'

const { confirm } = Modal
const { Column } = Table
const { Search } = Input
const { Option } = Select
const { RangePicker } = DatePicker

export interface ISearchState {
  searchText: string | undefined
  start: any | undefined
  end: any | undefined
}

const ContactPage = () => {
  const { scope } = useAuth()
  const { setPageHeader } = useGlobalStore()
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [dataSource, setDataSource] = useState<Contact>()
  const [contacts, setContacts] = useState<Contact[]>([])

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 15,
    total: 0,
  })

  const [sorter] = useState({
    orderby: 'createdAt',
    sort: 'desc',
  })

  const [search, setSearch] = useState<ISearchState>({
    searchText: undefined,
    start: undefined,
    end: undefined,
  })

  const [loading, setLoading] = useState<boolean>(false)

  useEffect(() => {
    setPageHeader({
      title: 'Contact Overview',
      subTitle: 'Content Management',
      backIcon: false,
      extra: [],
    })
    findAll()
  }, [])

  const findAll = async (param = {}) => {
    setLoading(true)
    const body = {
      ...pagination,
      ...sorter,
      search,
      ...param,
    }
    const payload: any = await findContacts(body)
    if (payload?.status?.code === 200) {
      setContacts(payload.data?.results)
      setPagination({
        limit: body.limit,
        page: payload.data?.pagination?.page,
        total: payload.data?.pagination?.total,
      })
    }
    setLoading(false)
  }

  const changePage = (page: number, limit: any) => {
    const paginate = {
      ...pagination,
      page,
      limit,
    }
    setPagination(paginate)
    findAll({ ...paginate, ...sorter })
  }

  const handelDelete = async (item) => {
    confirm({
      title: 'Are you sure?',
      icon: <ExclamationCircleOutlined />,
      content: 'Permanently delete this data',
      async onOk() {
        try {
          setLoading(true)
          const payload = await deleteContact(item.id)
          if (payload.status?.code === 200) {
            message.success('Deleted Successfully')
            findAll()
          }
          setLoading(false)
        } catch (error) {
          setLoading(false)
        }
      },
    })
  }

  const onSearch = (value) => {
    const searching = {
      ...search,
      searchText: value,
    }
    setSearch(searching)
    findAll({
      search: searching,
    })
  }

  const clearSearch = () => {
    const searching = {
      ...search,
      searchText: undefined,
      start: undefined,
      end: undefined,
    }
    setSearch(searching)
    findAll({
      search: searching,
    })
  }

  const onChangeDateRange = (value: any) => {
    const searching = {
      ...search,
      start: value[0],
      end: value[1],
    }
    setSearch(searching)
    findContacts({
      search: {
        ...searching,
        start: dayjs(value[0]).format('YYYY-MM-DD'),
        end: dayjs(value[1]).format('YYYY-MM-DD'),
      },
    })
  }

  return (
    <>
      <Row gutter={16}>
        <Col span={24}>
          <Space className='flex' align='start'>
            <RangePicker
              size='large'
              allowClear={false}
              onChange={onChangeDateRange}
              value={[search.start, search.end]}
            />

            <Search
              allowClear
              size='large'
              placeholder='Search..'
              onSearch={onSearch}
              onChange={(event) =>
                setSearch({
                  ...search,
                  searchText: event.target.value,
                })
              }
              value={search.searchText}
              style={{ width: 300 }}
              disabled={loading}
            />

            <Button
              type='link'
              onClick={clearSearch}
              disabled={!search.searchText && !search.start && !search.end}
            >
              clear
            </Button>
          </Space>
        </Col>
        <Col span={24}>
          <Table
            loading={loading}
            pagination={false}
            rowKey={(record) => `contact-${record?.id + ''}`}
            dataSource={contacts}
            className='mt-2 mb-2'
          >
            <Column
              title='Information'
              className='break-word'
              ellipsis
              render={(record) => (
                <>
                  <div className='text-md font-bold flex'>
                    {record?.name} <span className='mx-3 border-l border-gray-300'></span>
                    <span className='flex justify-between items-center'>
                      <MailOutlined className='mt mr-1' />
                      {record?.email}
                    </span>
                  </div>
                  <div className='flex gap-2 text-sm truncate text-gray-500'>
                    <Tooltip title={record.title}>
                      <p className='text-gray-500'>{record.title}</p>
                    </Tooltip>
                    :
                    <Tooltip title={record.message}>
                      <p className='text-sm truncate text-gray-500'>{record.message}</p>
                    </Tooltip>
                  </div>
                </>
              )}
            />

            <Column
              title='Created'
              className='break-word'
              align='center'
              width={130}
              render={(record) => (
                <div className='text-center'>
                  {dayjs(record?.createdAt).format('DD-MM-YYYY hh:mm:ss A')}
                </div>
              )}
            />

            <Column
              width={100}
              render={(record: Contact) => (
                <PermissionAccess permission='contact:update' scope={scope}>
                  <div className='flex justify-between'>
                    <Tooltip title='Delete'>
                      <Button
                        type='dashed'
                        icon={<DeleteOutlined />}
                        shape='circle'
                        className='text-primary-orange'
                        onClick={() => {
                          handelDelete(record)
                        }}
                      />
                    </Tooltip>
                  </div>
                </PermissionAccess>
              )}
            />
          </Table>

          <Pagination
            showSizeChanger
            onChange={changePage}
            onShowSizeChange={changePage}
            pageSize={pagination.limit}
            current={pagination.page}
            total={pagination.total}
            className='text-right'
          />
        </Col>
      </Row>
    </>
  )
}

export default ContactPage
